import { Button } from 'react-bootstrap';
import { BsDownload, BsExclamationTriangle } from 'react-icons/bs';
import styled from 'styled-components';
import { ExportStatus } from '../../helpers/constants';
import type { HistoryFetchStatus } from '../../types/exportTypes';
import type { TransformedExportRecord } from '../../utils/exportDataTransformers';
import LocationsDisplay from './locationsCollapse';

interface ExportHistoryTableProps {
  records: TransformedExportRecord[];
  historyFetchStatus: HistoryFetchStatus;
  onDownload: (url: string) => void;
}

const ExportHistoryTable = ({
  records,
  historyFetchStatus,
  onDownload
}: ExportHistoryTableProps) => {
  if (historyFetchStatus.error) {
    return (
      <StyledTable>
        <div className="error-state">
          <BsExclamationTriangle className="error-icon" />
          <h4>Error Loading Export History</h4>
          <p>{historyFetchStatus.error}</p>
        </div>
      </StyledTable>
    );
  }

  return (
    <StyledTable>
      <div className="table-container">
        {historyFetchStatus.isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner" />
            <span>Loading export history...</span>
          </div>
        )}

        <table className="export-table">
          <thead>
            <tr>
              <th>Report Type</th>
              <th>Generated At</th>
              <th>Generated By</th>
              <th>Time Range</th>
              <th>Locations</th>
              <th>Valid Until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !historyFetchStatus.isLoading ? (
              <tr>
                <td colSpan={8} className="no-data">
                  No export history found
                </td>
              </tr>
            ) : (
              records.map((record) => (
                <tr key={record.id}>
                  <td className="report-type" data-label="Report Type">
                    {record.report_type}
                  </td>
                  <td className="generated-at" data-label="Generated At">
                    {record.generated_at}
                  </td>
                  <td className="generated-by" data-label="Generated By">
                    {record.generated_by}
                  </td>
                  <td className="time-range" data-label="Time Range">
                    {record.time_range}
                  </td>
                  <td className="locations" data-label="Locations">
                    <LocationsDisplay
                      locations={record.locations || 'All Locations'}
                      maxLength={25}
                    />
                  </td>
                  <td
                    className={`expiry-date ${record.isExpired ? 'expired' : ''}`}
                    data-label="Expiry Date"
                  >
                    {record.expiry_date}
                  </td>
                  <td className="actions" data-label="Actions">
                    {record.download_url &&
                    record.raw_status === ExportStatus.COMPLETED &&
                    !record.isExpired ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onDownload(record.download_url!)}
                        className="download-btn"
                      >
                        <BsDownload />
                        Download
                      </Button>
                    ) : record.download_url &&
                      record.raw_status === ExportStatus.COMPLETED &&
                      record.isExpired ? null : record.raw_status === ExportStatus.PENDING ? (
                      <span className="na-text">-</span>
                    ) : record.raw_status === ExportStatus.ERROR ? (
                      <span className="na-text">-</span>
                    ) : (
                      <span className="na-text">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </StyledTable>
  );
};

export default ExportHistoryTable;

const StyledTable = styled.div`
  .error-state {
    text-align: center;
    padding: 40px;

    .error-icon {
      font-size: 48px;
      color: #dc3545;
      margin-bottom: 15px;
    }

    h4 {
      color: #dc3545;
      margin-bottom: 10px;
    }

    p {
      color: #666;
    }
  }

  .table-container {
    position: relative;
    overflow-x: auto;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid ${({ theme }) => theme.border_color};
    min-height: 400px;

    /* Mobile responsive */
    @media (max-width: 767px) {
      overflow-x: visible;
      min-height: auto;
    }

    /* Tablet responsive */
    @media (min-width: 768px) and (max-width: 1199px) {
      overflow-x: auto;
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #91a1ac;
      font-weight: 500;
      backdrop-filter: blur(10px);
      z-index: 2;
      border-radius: 8px;

      .loading-spinner {
        width: 24px;
        height: 24px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-right: 10px;
      }

      span {
        color: #91a1ac;
        font-size: 14px;
      }
    }
  }

  .export-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    /* Default column widths for medium screens (768px - 1199px) */
    @media (min-width: 768px) and (max-width: 1199px) {
      min-width: 100%;
      table-layout: auto;
    }

    /* Large desktop layout */
    @media (min-width: 1200px) {
      table-layout: fixed;
    }

    /* Mobile responsive - Convert to card layout */
    @media (max-width: 767px) {
      display: block;
      border-collapse: separate;
      border-spacing: 0;

      thead {
        display: none;
      }

      tbody {
        display: block;
        width: 100%;
      }

      tr {
        display: block;
        margin-bottom: 16px;
        background: ${({ theme }) => theme.widget.background};
        border: 1px solid ${({ theme }) => theme.border_color};
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        &:last-child {
          margin-bottom: 0;
        }
      }

      td {
        display: block;
        width: 100%;
        padding: 8px 0 !important;
        border: none !important;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.border_color} !important;

        &:last-child {
          border-bottom: none !important;
        }

        &:before {
          content: attr(data-label);
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
          display: block;
          margin-bottom: 4px;
          font-size: 12px;
          text-transform: uppercase;
        }

        &.no-data {
          text-align: center;
          padding: 40px 16px !important;
          border: none !important;

          &:before {
            display: none;
          }
        }
      }
    }

    /* Tablet responsive - Adjust table layout */
    @media (min-width: 768px) and (max-width: 1199px) {
      min-width: 1000px;

      th,
      td {
        padding: 10px !important;
        font-size: 12px !important;
      }
    }

    thead {
      background: ${({ theme }) => theme.widget.tableHeader};

      th {
        color: ${({ theme }) => theme.text};
        font-weight: 600;
        text-align: left;
        padding: 12px;
        border-bottom: 2px solid ${({ theme }) => theme.border_color};
        transition: background-color 0.2s;
        font-size: 13px;

        /* Desktop column widths */
        @media (min-width: 1200px) {
          &:nth-child(1) {
            width: 28%;
          } /* Report Type */
          &:nth-child(2) {
            width: 10%;
          } /* Generated At */
          &:nth-child(3) {
            width: 12%;
          } /* Generated By */
          &:nth-child(4) {
            width: 18%;
          } /* Time Range - wider for date range */
          &:nth-child(5) {
            width: 14%;
          } /* Locations */
          &:nth-child(6) {
            width: 8%;
          } /* Status */
          &:nth-child(7) {
            width: 10%;
          } /* Expiry Date */
          &:nth-child(8) {
            width: 10%;
          } /* Actions */
        }

        /* Tablet column widths */
        @media (min-width: 768px) and (max-width: 1199px) {
          &:nth-child(1) {
            width: 25%;
            min-width: 200px;
          } /* Report Type */
          &:nth-child(2) {
            width: 10%;
            min-width: 90px;
          } /* Generated At */
          &:nth-child(3) {
            width: 12%;
            min-width: 100px;
          } /* Generated By */
          &:nth-child(4) {
            width: 16%;
            min-width: 140px;
          } /* Time Range */
          &:nth-child(5) {
            width: 12%;
            min-width: 100px;
          } /* Locations */
          &:nth-child(6) {
            width: 8%;
            min-width: 80px;
          } /* Status */
          &:nth-child(7) {
            width: 9%;
            min-width: 90px;
          } /* Expiry Date */
          &:nth-child(8) {
            width: 8%;
            min-width: 80px;
          } /* Actions */
        }
      }
    }

    tbody {
      tr {
        transition: background-color 0.2s;

        &:nth-child(even) {
          background: ${({ theme }) => theme.widget_group.background};
        }

        &:hover {
          background: ${({ theme }) => theme.widget_group.background};
        }
      }

      td {
        padding: 12px;
        color: ${({ theme }) => theme.text};
        border-bottom: 1px solid ${({ theme }) => theme.table_cell_border};
        font-size: 13px;
        vertical-align: top;

        &.no-data {
          text-align: center;
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          font-style: italic;
          padding: 40px;
        }

        &.report-type {
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          white-space: normal;
          word-wrap: break-word;
          line-height: 1.4;
        }

        &.generated-at,
        &.expiry-date {
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
          white-space: nowrap;

          &.expired {
            color: #dc3545;
            font-weight: 600;
            opacity: 1;
          }
        }

        &.generated-by {
          color: ${({ theme }) => theme.text};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &.time-range {
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
          white-space: normal;
          word-wrap: break-word;
          line-height: 1.3;
        }

        &.locations {
          color: ${({ theme }) => theme.text};
          opacity: 0.8;
        }

        &.status {
          white-space: nowrap;

          /* Mobile responsive status */
          @media (max-width: 767px) {
            .status-badge {
              display: inline-block;
              width: auto;
              text-align: center;
              margin-top: 4px;
            }
          }

          .status-badge {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;

            &.completed {
              background: #d4edda;
              color: #155724;
            }

            &.pending {
              background: #fff3cd;
              color: #856404;
            }

            &.error,
            &.failed {
              background: #f8d7da;
              color: #721c24;
            }

            &.unknown {
              background: #e2e3e5;
              color: #6c757d;
            }
          }
        }

        &.actions {
          white-space: nowrap;
          text-align: center;
          /* Center align all content including buttons */
          display: flex;
          justify-content: center;
          align-items: center;

          /* Mobile responsive actions */
          @media (max-width: 767px) {
            white-space: normal;

            .download-btn {
              width: 100%;
              justify-content: center;
              margin-top: 8px;
            }

            .pending-text {
              display: block;
              width: 100%;
              text-align: center;
              padding: 8px;
              background: #fff3cd;
              color: #856404;
              border-radius: 4px;
              margin-top: 8px;
            }
          }

          .download-btn {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 12px;
            padding: 4px 12px;

            &:active,
            &:focus,
            &.active {
              /* Remove active state styling - let it behave normally */
              background-color: initial;
              border-color: initial;
              color: initial;
              box-shadow: none;
            }
          }

          .expired-btn {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 12px;
            padding: 4px 12px;
            border-color: #dc3545 !important;
            color: #dc3545 !important;
            background-color: transparent !important;
            cursor: not-allowed;
            opacity: 0.6;

            &:hover {
              background-color: transparent !important;
              border-color: #dc3545 !important;
              color: #dc3545 !important;
            }

            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }
          }

          .pending-text {
            color: #856404;
            font-size: 12px;
            font-style: italic;
          }

          .error-text {
            color: #721c24;
            font-size: 12px;
            font-style: italic;
          }

          .na-text {
            color: #6c757d;
            font-size: 12px;
            font-style: italic;
          }
        }
      }
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1200px) {
    .export-table {
      thead th {
        padding: 10px;
        font-size: 13px;
      }

      tbody td {
        padding: 10px;
        font-size: 13px;
      }
    }
  }

  @media (max-width: 768px) {
    .export-table {
      thead th {
        padding: 8px;
        font-size: 13px;
      }

      tbody td {
        padding: 8px;
        font-size: 13px;
      }
    }
  }
`;
